import { type ReactNode, useEffect, useRef, useState } from 'react';

import {
  MAX_EDITORS_CHOICE_HEADLINE_LENGTH,
  MAX_EDITORS_CHOICE_TAG_LENGTH,
} from '../../../constants/form';
import { cn } from '../../../lib/utils';
import { Button } from '../../Button';
import * as Form from '../../Form';
import {
  CharacterCountHint,
  charactersLeft,
} from '../../shared/CharacterCountHint';
import type { DappFormInputChangeEvent } from '../types';

interface EditorsChoiceSectionBaseProps {
  headline: string;
  tag: string;
  /** True when a graphic (new file or saved URL) exists. */
  hasGraphic: boolean;
  onInputChange: (event: DappFormInputChangeEvent) => void;
  graphicField: ReactNode;
}

export const EDITORS_CHOICE_DESCRIPTION =
  'The dApp Store home screen includes a featured carousel showcasing dApps we believe are valuable for Seeker users. Solana Mobile editors periodically select which dApps to highlight.';

/**
 * Editor's Choice opt-in block shared by the Store Listing tab and the New
 * dApp drawer: "Apply" reveals the headline / tag / graphic fields.
 */
export function EditorsChoiceSectionBase({
  headline,
  tag,
  hasGraphic,
  onInputChange,
  graphicField,
}: EditorsChoiceSectionBaseProps) {
  const hasData = Boolean(headline || tag || hasGraphic);
  // Once opted in (or loaded with data) the fields stay mounted, even if the
  // publisher clears every value mid-edit.
  const [applied, setApplied] = useState(hasData);
  useEffect(() => {
    if (hasData) setApplied(true);
  }, [hasData]);
  const showFields = applied || hasData;
  // Only the "Apply" click reveals the fields with motion; data-loaded mounts don't.
  const revealedByClick = useRef(false);

  return (
    <section className="flex flex-col gap-10">
      <div className="flex flex-col gap-2">
        <div
          role="heading"
          aria-level={2}
          className="text-4xl font-medium leading-10 text-white"
        >
          Editor&apos;s Choice
        </div>
        <p className="text-sm text-seeker-400">
          {EDITORS_CHOICE_DESCRIPTION}
          {showFields ? null : ' Interested in being featured?'}
        </p>
      </div>

      {showFields ? (
        <div
          className={cn(
            'flex flex-col gap-6',
            revealedByClick.current && 'motion-enter',
          )}
        >
          <div className="flex flex-col gap-1">
            <Form.Input
              label="Headline"
              explanation="To promote your dApp beyond the description."
              name="editorsChoiceHeadline"
              value={headline}
              onChange={onInputChange}
              maxLength={MAX_EDITORS_CHOICE_HEADLINE_LENGTH}
            />
            <CharacterCountHint
              className="text-left tabular-nums"
              text={charactersLeft(
                headline,
                MAX_EDITORS_CHOICE_HEADLINE_LENGTH,
              )}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Form.Input
              label="Tag"
              explanation="To add flavor to your dApp's module."
              name="editorsChoiceTag"
              value={tag}
              onChange={onInputChange}
              maxLength={MAX_EDITORS_CHOICE_TAG_LENGTH}
            />
            <CharacterCountHint
              className="text-left tabular-nums"
              text={charactersLeft(tag, MAX_EDITORS_CHOICE_TAG_LENGTH)}
            />
          </div>

          {graphicField}
        </div>
      ) : (
        <Button
          variant="primary"
          className="w-[180px]"
          onClick={() => {
            revealedByClick.current = true;
            setApplied(true);
          }}
        >
          Apply
        </Button>
      )}
    </section>
  );
}
